import React, { useState } from "react";
import { LuCheck, LuCopy, LuMessageCircle } from "react-icons/lu";
import { Badge, Button, Dialog, Field, Textarea, cn } from "../../components/ui";
import { StepsBar } from "../../components/patterns/StepsBar";
import { useSession } from "../../context/SessionContext";
import { showSuccess } from "../../lib/feedback";
import { t } from "../../i18n";
import { Notice, Section } from "./parts";
import { buildDefaultWhatsAppMessage, buildWhatsAppUrl } from "./whatsapp";
import { phoneError } from "../../lib/validators";

interface WhatsAppComposerProps {
  onClose: () => void;
  patientName?: string;
}

type PhoneInputProps = React.InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean };

// O "+55" fica fora do valor: o médico digita só DDD + número.
const PhoneInput: React.FC<PhoneInputProps> = ({ invalid, ...rest }) => (
  <div
    className={cn(
      "flex h-control-touch items-stretch overflow-hidden rounded-control border bg-surface transition-shadow focus-within:border-whatsapp focus-within:shadow-focus sm:h-control",
      invalid ? "border-danger" : "border-line-strong"
    )}
  >
    <span className="flex select-none items-center border-r border-line bg-surface-muted px-3 font-semibold text-fg-muted">+55</span>
    <input type="tel" inputMode="numeric" autoComplete="tel" aria-invalid={invalid || undefined} className="min-w-0 flex-1 bg-transparent px-3.5 text-body text-fg outline-none placeholder:text-fg-subtle" {...rest} />
  </div>
);

/** "Enviar pelo WhatsApp" em 2 passos: número e mensagem → revisão → abre o wa.me. */
export const WhatsAppComposer: React.FC<WhatsAppComposerProps> = ({ onClose, patientName }) => {
  const { metadata } = useSession();
  const [step, setStep] = useState<1 | 2>(1);
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState(() => buildDefaultWhatsAppMessage(metadata.nome, patientName));
  // O erro só aparece depois da primeira tentativa de continuar.
  const [tried, setTried] = useState(false);
  const [copy, setCopy] = useState<"idle" | "copied" | "error">("idle");

  const error = phoneError(phone);
  const url = buildWhatsAppUrl(phone, message);

  const handleContinue = () => {
    setTried(true);
    if (!error) setStep(2);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopy("copied");
      setTimeout(() => setCopy("idle"), 2000);
    } catch {
      setCopy("error");
    }
  };

  const handleOpen = () => {
    window.open(url, "_blank", "noopener,noreferrer");
    showSuccess("whatsAppOpened");
    onClose();
  };

  const footer =
    step === 1 ? (
      <>
        <Button variant="outline" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        {/* Sem `disabled`: o clique é o que revela o erro do número. */}
        <Button variant="whatsapp" className={cn(error && "opacity-50")} onClick={handleContinue}>
          {t("sharing.continue")}
        </Button>
      </>
    ) : (
      <>
        <Button variant="outline" onClick={() => setStep(1)}>
          {t("common.back")}
        </Button>
        <Button variant="whatsapp" icon={<LuMessageCircle size={18} aria-hidden />} onClick={handleOpen}>
          {t("sharing.whatsApp.open")}
        </Button>
      </>
    );

  return (
    <Dialog
      open
      onClose={onClose}
      title={t("sharing.whatsApp.title")}
      description={<StepsBar step={step} tone="whatsapp" label={t(step === 1 ? "sharing.whatsApp.composeStep" : "sharing.whatsApp.reviewStep")} />}
      footer={footer}
    >
      {step === 1 ? (
        <div className="flex flex-col gap-4">
          <Field
            label={t("sharing.whatsApp.phoneLabel")}
            hint={t("sharing.whatsApp.phoneHint")}
            error={tried ? error : undefined}
          >
            <PhoneInput
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleContinue()}
              placeholder={t("sharing.whatsApp.phonePlaceholder")}
            />
          </Field>
          <Field
            label={
              <span className="flex items-center justify-between gap-2">
                {t("sharing.messageLabel")}
                <Badge tone="warning">{t("sharing.editableTemplate")}</Badge>
              </span>
            }
          >
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={8} className="resize-y leading-relaxed" />
          </Field>
          <Notice>{t("sharing.whatsApp.attachNotice")}</Notice>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <p className="text-caption text-fg-muted">{t("sharing.whatsApp.reviewIntro")}</p>
          <Section title={t("sharing.whatsApp.recipient")}>
            <span className="self-start">
              <Badge tone="success">+55 {phone}</Badge>
            </span>
          </Section>
          <Section title={t("sharing.messagePreview")}>
            <div className="flex rounded-card bg-surface-muted p-3.5">
              <p className="ml-auto max-w-[92%] whitespace-pre-line break-words rounded-card rounded-tr-none bg-whatsapp-soft px-3 py-2.5 text-caption text-fg shadow-card">
                {message}
              </p>
            </div>
          </Section>
          <Section title={t("sharing.whatsApp.link")}>
            <div className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate rounded-control border border-line bg-surface-muted px-3 py-2.5 text-caption text-fg-muted">{url}</span>
              <Button
                variant="outline"
                size="sm"
                icon={copy === "copied" ? <LuCheck size={16} aria-hidden /> : <LuCopy size={16} aria-hidden />}
                onClick={handleCopy}
              >
                {t(copy === "copied" ? "sharing.whatsApp.copied" : "sharing.whatsApp.copy")}
              </Button>
            </div>
            {copy === "error" && <p className="text-caption text-danger">{t("sharing.whatsApp.copyFailed")}</p>}
          </Section>
          <Notice>{t("sharing.whatsApp.reviewNotice")}</Notice>
        </div>
      )}
    </Dialog>
  );
};
